import {
  Home,
  Banknote,
  LayoutGrid,
  Bath,
  Building2,
  Train,
  FileText,
  RotateCcw,
  Check,
  ChefHat,
  Shield,
  Star,
  Clock,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { FilterSection } from "./FilterSection";
import { CustomSelect } from "../CustomSelect";
import { RangeSlider } from "../RangeSlider";
import { useTranslation } from "../../hooks/useTranslation";
import {
  FILTER_CATEGORIES,
  DEFAULT_FILTER_STATE,
  type FilterState,
} from "../../constants/filters";

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  Home,
  Banknote,
  LayoutGrid,
  Bath,
  Building2,
  Train,
  FileText,
  ChefHat,
  Shield,
  Star,
  Clock,
};

interface FilterPanelProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onClose?: () => void;
}

export function FilterPanel({
  filters,
  onFiltersChange,
  onClose,
}: FilterPanelProps) {
  const t = useTranslation();

  const handleCheckboxChange = (filterId: string, checked: boolean) => {
    onFiltersChange({
      ...filters,
      [filterId]: checked,
    });
  };

  const handleSelectChange = (filterId: string, value: string | number) => {
    onFiltersChange({
      ...filters,
      [filterId]: value,
    });
  };

  const handleRangeChange = (
    minId: string,
    maxId: string,
    minValue: number,
    maxValue: number,
  ) => {
    onFiltersChange({
      ...filters,
      [minId]: minValue,
      [maxId]: maxValue,
    });
  };

  const handleMultiselectChange = (filterId: string, value: string) => {
    const currentValues =
      (filters[filterId as keyof FilterState] as string[]) || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];

    onFiltersChange({
      ...filters,
      [filterId]: newValues,
    });
  };

  const handleReset = () => {
    onFiltersChange(DEFAULT_FILTER_STATE);
  };

  // Format value based on type
  const getFormatFunction = (formatType?: "currency" | "area" | "minutes") => {
    switch (formatType) {
      case "currency":
        return (value: number) => `¥${value.toLocaleString()}`;
      case "area":
        return (value: number) => `${value}m²`;
      case "minutes":
        return (value: number) => `${value}min`;
      default:
        return undefined;
    }
  };

  const renderFilter = (
    filter: (typeof FILTER_CATEGORIES)[0]["filters"][0],
  ) => {
    switch (filter.type) {
      case "checkbox":
        return (
          <label
            key={filter.id}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="relative">
              <input
                type="checkbox"
                checked={filters[filter.id as keyof FilterState] as boolean}
                onChange={(e) =>
                  handleCheckboxChange(filter.id, e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-5 h-5 border-2 border-gray-300 rounded peer-checked:border-red-600 peer-checked:bg-red-600 transition-colors flex items-center justify-center">
                {filters[filter.id as keyof FilterState] && (
                  <Check className="w-3.5 h-3.5 text-white" />
                )}
              </div>
            </div>
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              {t(filter.labelKey as keyof ReturnType<typeof useTranslation>)}
            </span>
          </label>
        );

      case "select":
        return (
          <div key={filter.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t(filter.labelKey as keyof ReturnType<typeof useTranslation>)}
            </label>
            <CustomSelect
              value={filters[filter.id as keyof FilterState] as string}
              onChange={(value) => handleSelectChange(filter.id, value)}
              options={
                filter.options?.map((option) => ({
                  value: option.value,
                  label: t(
                    option.labelKey as keyof ReturnType<typeof useTranslation>,
                  ),
                })) || []
              }
              placeholder={t(
                filter.labelKey as keyof ReturnType<typeof useTranslation>,
              )}
              compact
            />
          </div>
        );

      case "dualRange":
        if (!filter.minId || !filter.maxId) return null;
        return (
          <div key={filter.id} className="space-y-2">
            <RangeSlider
              min={filter.min ?? 0}
              max={filter.max ?? 100}
              step={filter.step ?? 1}
              minValue={filters[filter.minId as keyof FilterState] as number}
              maxValue={filters[filter.maxId as keyof FilterState] as number}
              unit={filter.unit}
              formatValue={getFormatFunction(filter.formatValue)}
              onChange={(minVal, maxVal) =>
                handleRangeChange(filter.minId!, filter.maxId!, minVal, maxVal)
              }
            />
          </div>
        );

      case "multiselect":
        return (
          <div key={filter.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t(filter.labelKey as keyof ReturnType<typeof useTranslation>)}
            </label>
            <div className="flex flex-wrap gap-2">
              {filter.options?.map((option) => {
                const currentValues =
                  (filters[filter.id as keyof FilterState] as string[]) || [];
                const isSelected = currentValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      handleMultiselectChange(filter.id, option.value)
                    }
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      isSelected
                        ? "bg-red-600 border-red-600 text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:border-red-300"
                    }`}
                  >
                    {t(
                      option.labelKey as keyof ReturnType<
                        typeof useTranslation
                      >,
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case "radio":
        return (
          <div key={filter.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t(filter.labelKey as keyof ReturnType<typeof useTranslation>)}
            </label>
            <div className="flex flex-wrap gap-2">
              {filter.options?.map((option) => {
                const isSelected =
                  filters[filter.id as keyof FilterState] === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelectChange(filter.id, option.value)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      isSelected
                        ? "bg-red-600 border-red-600 text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:border-red-300"
                    }`}
                  >
                    {t(
                      option.labelKey as keyof ReturnType<
                        typeof useTranslation
                      >,
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case "range":
        return (
          <div key={filter.id} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {t(filter.labelKey as keyof ReturnType<typeof useTranslation>)}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={filters[filter.id as keyof FilterState] as number}
                onChange={(e) => handleSelectChange(filter.id, e.target.value)}
                min={filter.min}
                max={filter.max}
                step={filter.step}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white"
              />
              {filter.unit && (
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {filter.unit}
                </span>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col">
      {/* Header - Fixed */}
      <div className="sticky top-0 z-10 bg-white px-4 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="font-bold text-gray-900">{t("filtersTitle")}</h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
          >
            <RotateCcw className="w-4 h-4" />
            {t("filterReset")}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Filter Sections */}
      <div>
        {FILTER_CATEGORIES.map((category) => {
          const Icon = iconMap[category.icon] || Home;
          return (
            <FilterSection
              key={category.id}
              title={t(
                category.labelKey as keyof ReturnType<typeof useTranslation>,
              )}
              icon={Icon}
              defaultOpen={
                category.id === "property" || category.id === "price"
              }
            >
              <div className="space-y-4 px-6">
                {category.filters.map(renderFilter)}
              </div>
            </FilterSection>
          );
        })}
      </div>
    </div>
  );
}
